/**
 * De standaard-regelset: de rekenlogica van De Klerk Binnenbouw, uitgeschreven als
 * bewerkingsregels.
 *
 * Dit is de omzetting van wat vroeger in code stond (`bouwTomtom` en `berekenMaten`)
 * naar het regelmodel van de blokkeneditor. Vanaf hier is dít de waarheid: de motor
 * voert deze regels uit, en wie ze in scherm 06 aanpast, verandert daarmee echt hoe de
 * applicatie rekent.
 *
 * De `id` van elke regel is ook de sleutel van de stap in de route. Die is met opzet
 * niet gewijzigd bij de omzetting: de afgestreepte stappen op de karren hangen eraan.
 *
 * NOG TE VALIDEREN met een afschrijver van De Klerk Binnenbouw (openstaand punt 3 van
 * de werkopdracht): de toeslagwaarden en de rekenvolgorde zijn afgeleid uit de huidige
 * houtstaat-template en coderingen. Ze staan nu op één plek en zijn zonder code aan te
 * passen — dat was het doel van deze fase.
 */
import { OVERMAAT_VOOR_PERSEN_MM, STANDAARD_OVERMAAT_MM } from './constanten.js';
import { ALTIJD } from './regels.js';
const OMGEZET_OP = '2026-08-10T00:00:00+02:00';
// ── Kleine bouwers, zodat de regels leesbaar blijven ────────────────────────
const veld = (pad) => ({ herkomst: 'veld', pad });
const tabel = (pad) => ({ herkomst: 'tabel', pad });
const vast = (waarde) => ({ herkomst: 'constant', waarde });
const param = (naam) => ({ herkomst: 'variabele', naam });
const plus = (...termen) => ({ herkomst: 'berekend', operator: 'plus', termen });
const min = (...termen) => ({ herkomst: 'berekend', operator: 'min', termen });
/** "Er staat fineer op zijde 0 of zijde 5." */
const HEEFT_FINEER = {
    soort: 'vergelijking',
    links: tabel('Fineer.Aanwezig'),
    vergelijker: 'is-waar',
};
const GEEN_FINEER = { soort: 'niet', kind: HEEFT_FINEER };
/** De gemeenschappelijke velden van elke regel. */
const basis = {
    bouwnummer: null,
    actief: true,
    normZichtbaarVoorProductie: false,
    fotoVerplicht: false,
    gewijzigdOp: OMGEZET_OP,
};
/**
 * "Als … dan … anders": de handmatig ingevulde waarde als die er is, en anders de
 * standaard. Precies het gedrag dat vroeger met `?? standaardOvermaat` in de code
 * stond — een handmatige overmaat vervángt de standaard, ook als hij kleiner is.
 */
const alsGevuld = (pad, anders) => ({
    herkomst: 'keuze',
    als: { soort: 'vergelijking', links: veld(pad), vergelijker: 'is-gevuld' },
    dan: veld(pad),
    anders,
});
export const STANDAARDREGELS = [
    // ── 0. ZAAGMAAT: de maten die op de materiaalstaat staan ──────────────────
    // Deze regel activeert geen bewerking; hij zet alleen de waarden die de rest
    // gebruikt. Zo staat "hoe komt de zaagmaat tot stand" op één plek in de editor,
    // in plaats van verspreid over de twee zaagstappen.
    {
        ...basis,
        id: 'zaagmaat',
        code: 'ZM',
        naam: 'ZAAGMAAT',
        soort: 'CNCZAAG',
        afdeling: 'Zagerij',
        kolomnaam: '(geen stap)',
        prioriteit: 5,
        normtijd: { opstartMin: 0, perStukMin: 0 },
        voorwaarde: ALTIJD,
        acties: [
            // Een geperst paneel wordt ruim voorgezaagd; een kaal paneel krijgt de
            // standaard zaagovermaat van de zagerij.
            {
                soort: 'parameter-instellen',
                parameter: 'basisovermaat',
                waarde: {
                    herkomst: 'keuze',
                    als: HEEFT_FINEER,
                    dan: vast(OVERMAAT_VOOR_PERSEN_MM),
                    anders: vast(STANDAARD_OVERMAAT_MM),
                },
            },
            {
                soort: 'waarde-berekenen',
                parameter: 'overmaatLengte',
                waarde: alsGevuld('onderdeel.overmaatLengte', param('basisovermaat')),
            },
            {
                soort: 'waarde-berekenen',
                parameter: 'overmaatBreedte',
                waarde: alsGevuld('onderdeel.overmaatBreedte', param('basisovermaat')),
            },
            {
                soort: 'waarde-berekenen',
                parameter: 'brutoLengte',
                waarde: plus(veld('onderdeel.lengte'), param('overmaatLengte'), tabel('Kant.Toeslag.Lengte'), veld('onderdeel.overmaatKarweiLengte')),
            },
            {
                soort: 'waarde-berekenen',
                parameter: 'brutoBreedte',
                waarde: plus(veld('onderdeel.breedte'), param('overmaatBreedte'), tabel('Kant.Toeslag.Breedte'), veld('onderdeel.overmaatKarweiBreedte')),
            },
        ],
        parameters: [
            {
                naam: 'basisovermaat',
                herkomst: 'keuze',
                uitleg: `Voorzagen vóór het persen (${OVERMAAT_VOOR_PERSEN_MM} mm) of de standaard zaagovermaat (${STANDAARD_OVERMAAT_MM} mm).`,
                eenheid: 'mm',
            },
            {
                naam: 'overmaatLengte',
                herkomst: 'keuze',
                uitleg: 'De handmatig ingevulde overmaat als die er is, anders de basisovermaat.',
                eenheid: 'mm',
            },
            { naam: 'overmaatBreedte', herkomst: 'keuze', uitleg: 'Idem, op de breedte.', eenheid: 'mm' },
            {
                naam: 'brutoLengte',
                herkomst: 'berekend',
                uitleg: 'Netto lengte + overmaat + kantenbandtoeslag + overmaat karwei.',
                eenheid: 'mm',
            },
            { naam: 'brutoBreedte', herkomst: 'berekend', uitleg: 'Idem, op de breedte.', eenheid: 'mm' },
        ],
        uitleg: 'Hoe de zaagmaat op de materiaalstaat tot stand komt. Deze regel is geen stap in de tomtom: ' +
            'hij zet alleen de waarden waar de zaagstappen en de materiaalstaat mee rekenen.',
    },
    // ── 1. FINEER 1 ───────────────────────────────────────────────────────────
    {
        ...basis,
        id: 'fineer-1',
        code: 'F1',
        naam: 'FINEER 1',
        soort: 'FINEER',
        afdeling: 'Fineerderij',
        kolomnaam: 'FINEER 1',
        prioriteit: 10,
        normtijd: { opstartMin: 6, perStukMin: 4 },
        voorwaarde: HEEFT_FINEER,
        acties: [
            { soort: 'activeer-bewerking' },
            { soort: 'label-tonen', patroon: 'FINEER' },
            { soort: 'toelichting-tonen', patroon: '{Fineer.Code.Zijde0} · {Fineer.Dikte.Zijde0} mm' },
        ],
        parameters: [],
        uitleg: 'Het fineer op zijde 0 gaat erop. Deze bewerking draait zodra er een afwerkingscode op ' +
            'zijde 0 of 5 staat die in de fineerdiktetabel voorkomt.',
    },
    // ── 2. WINKELEN ───────────────────────────────────────────────────────────
    {
        ...basis,
        id: 'winkelen',
        code: 'WK',
        naam: 'WINKELEN',
        soort: 'WINKELEN',
        afdeling: 'Winkel',
        kolomnaam: 'WINKELEN',
        prioriteit: 20,
        normtijd: { opstartMin: 3, perStukMin: 0.5 },
        voorwaarde: ALTIJD,
        acties: [
            { soort: 'activeer-bewerking' },
            // Winkelen blijft leeg in de tomtom: alleen de gekleurde kop. De toelichting
            // staat in het stap-menu.
            { soort: 'toelichting-tonen', patroon: 'Materiaal {materiaal} uit voorraad halen.' },
        ],
        parameters: [],
        uitleg: 'Het materiaal uit voorraad halen. Elk onderdeel gaat langs de winkel.',
    },
    // ── 3. CNC ZAAG, voorzagen vóór het persen ────────────────────────────────
    {
        ...basis,
        id: 'cnczaag-bruto',
        code: 'CZ',
        naam: 'CNCZ ARDIS B/N',
        soort: 'CNCZAAG',
        afdeling: 'Zagerij',
        kolomnaam: 'CNCZ ARDIS B/N',
        prioriteit: 30,
        normtijd: { opstartMin: 2, perStukMin: 1.5 },
        voorwaarde: HEEFT_FINEER,
        acties: [
            { soort: 'activeer-bewerking' },
            // De zaagafdeling zaagt hier voor op netto + de voorzaagovermaat. Bewust
            // zónder kantenband en karwei: die komen er pas bij de netto zaagsnede aan te
            // pas, en zo stond het ook in de tomtom vóór de omzetting naar regels.
            {
                soort: 'waarde-berekenen',
                parameter: 'zaagLengte',
                waarde: plus(veld('onderdeel.lengte'), vast(OVERMAAT_VOOR_PERSEN_MM)),
            },
            {
                soort: 'waarde-berekenen',
                parameter: 'zaagBreedte',
                waarde: plus(veld('onderdeel.breedte'), vast(OVERMAAT_VOOR_PERSEN_MM)),
            },
            { soort: 'label-tonen', patroon: '{aantal} × {zaagLengte} × {zaagBreedte}' },
            { soort: 'toelichting-tonen', patroon: `+${OVERMAAT_VOOR_PERSEN_MM} mm overmaat` },
        ],
        parameters: [
            {
                naam: 'zaagLengte',
                herkomst: 'berekend',
                uitleg: `Netto lengte + ${OVERMAAT_VOOR_PERSEN_MM} mm voorzaagovermaat.`,
                eenheid: 'mm',
            },
            { naam: 'zaagBreedte', herkomst: 'berekend', uitleg: 'Idem, op de breedte.', eenheid: 'mm' },
        ],
        uitleg: 'Voorzagen vóór het persen. Het paneel wordt ruim gezaagd omdat het na het persen alsnog ' +
            'op netto maat gaat.',
    },
    // ── 4. SCHUURMACHINE: kalibreren op kerndikte ─────────────────────────────
    {
        ...basis,
        id: 'schuurmachine',
        code: 'CA',
        naam: 'SCHUURMACHINE',
        soort: 'SCHUURMACHINE',
        afdeling: 'Schuurmachine',
        kolomnaam: 'SCHUURMACHINE',
        prioriteit: 40,
        normtijd: { opstartMin: 4, perStukMin: 3 },
        voorwaarde: HEEFT_FINEER,
        acties: [
            { soort: 'activeer-bewerking' },
            {
                soort: 'waarde-berekenen',
                parameter: 'calibreerdikte',
                waarde: min(veld('onderdeel.dikte'), tabel('Fineer.Dikte.Totaal')),
            },
            { soort: 'label-tonen', patroon: 'CALI {calibreerdikte} mm' },
            { soort: 'toelichting-tonen', patroon: 'netto − fineer' },
        ],
        parameters: [
            {
                naam: 'calibreerdikte',
                herkomst: 'berekend',
                uitleg: 'Netto dikte min de fineerdikte op zijde 0 én 5: de kern moet zo dun zijn dat het ' +
                    'paneel ná het persen op netto dikte uitkomt.',
                eenheid: 'mm',
            },
        ],
        uitleg: 'Kalibreren en schuren zijn één afdeling: de schuurmachine. De kern wordt teruggebracht ' +
            'tot netto dikte min de fineerlagen.',
    },
    // ── 5. FINEER 2 ───────────────────────────────────────────────────────────
    {
        ...basis,
        id: 'fineer-2',
        code: 'F2',
        naam: 'FINEER 2',
        soort: 'FINEER',
        afdeling: 'Fineerderij',
        kolomnaam: 'FINEER 2',
        prioriteit: 50,
        normtijd: { opstartMin: 6, perStukMin: 4 },
        voorwaarde: HEEFT_FINEER,
        acties: [
            { soort: 'activeer-bewerking' },
            { soort: 'label-tonen', patroon: 'FINEER' },
            { soort: 'toelichting-tonen', patroon: '{Fineer.Code.Zijde5} · {Fineer.Dikte.Zijde5} mm' },
        ],
        parameters: [],
        uitleg: 'Het fineer op zijde 5, de tegenzijde.',
    },
    // ── 6. PERS ───────────────────────────────────────────────────────────────
    {
        ...basis,
        id: 'pers',
        code: 'PE',
        naam: 'PERS',
        soort: 'PERS',
        afdeling: 'Pers',
        kolomnaam: 'PERS',
        prioriteit: 60,
        normtijd: { opstartMin: 40, perStukMin: 2 },
        voorwaarde: HEEFT_FINEER,
        acties: [{ soort: 'activeer-bewerking' }, { soort: 'label-tonen', patroon: 'PERS' }],
        parameters: [],
        uitleg: 'Het fineer wordt op de kern geperst. Bij deze bewerking staat de fotoplicht standaard ' +
            'aan; dat is per kar en per stap in te stellen.',
    },
    // ── 7. CNC ZAAG, op netto maat ────────────────────────────────────────────
    {
        ...basis,
        id: 'cnczaag-netto',
        code: 'CZ',
        naam: 'CNCZ NETTO',
        soort: 'CNCZAAG',
        afdeling: 'Zagerij',
        kolomnaam: 'CNCZ NETTO',
        prioriteit: 70,
        normtijd: { opstartMin: 2, perStukMin: 1.5 },
        voorwaarde: ALTIJD,
        acties: [
            { soort: 'activeer-bewerking' },
            /*
             * Op netto maat. LET OP — dit is de bestaande werkwijze, één op één overgenomen
             * bij de omzetting naar regels: de tomtom toont hier de nétto maat, terwijl de
             * materiaalstaat een zaagmaat mét overmaat, kantenband en karwei laat zien
             * (`brutoLengte`/`brutoBreedte` uit de regel ZAAGMAAT). Voor een geperst paneel
             * is dat logisch — de overmaat is er bij het voorzagen al af — maar voor een
             * kaal paneel wijken de twee getallen van elkaar af. Dit punt staat op de lijst
             * voor de afschrijver; het is met deze regel zonder code aan te passen.
             */
            { soort: 'waarde-berekenen', parameter: 'zaagLengte', waarde: veld('onderdeel.lengte') },
            { soort: 'waarde-berekenen', parameter: 'zaagBreedte', waarde: veld('onderdeel.breedte') },
            { soort: 'label-tonen', patroon: '{aantal} × {zaagLengte} × {zaagBreedte}' },
            { soort: 'toelichting-tonen', patroon: 'netto maat' },
        ],
        parameters: [
            { naam: 'zaagLengte', herkomst: 'veld', uitleg: 'De nettolengte zoals getekend.', eenheid: 'mm' },
            { naam: 'zaagBreedte', herkomst: 'veld', uitleg: 'De nettobreedte zoals getekend.', eenheid: 'mm' },
        ],
        uitleg: 'Zagen op netto maat. Bij een geperst paneel is dit de tweede zaagbewerking; bij een kaal ' +
            'paneel de enige.',
    },
    // ── 8. CONTROLE ───────────────────────────────────────────────────────────
    {
        ...basis,
        id: 'controle',
        code: 'CO',
        naam: 'CONTROLE',
        soort: 'CONTROLE',
        afdeling: 'Controle',
        kolomnaam: 'CONTROLE',
        prioriteit: 80,
        normtijd: { opstartMin: 2, perStukMin: 0.5 },
        voorwaarde: ALTIJD,
        acties: [{ soort: 'activeer-bewerking' }],
        parameters: [],
        uitleg: 'Blijft leeg in de tomtom, maar staat er wél als stap: de kar moet erlangs.',
    },
    // ── 9. GEREED ─────────────────────────────────────────────────────────────
    {
        ...basis,
        id: 'gereed',
        code: 'GE',
        naam: 'GEREED',
        soort: 'GEREED',
        afdeling: 'Gereed',
        kolomnaam: 'GEREED',
        prioriteit: 90,
        normtijd: { opstartMin: 0, perStukMin: 0 },
        voorwaarde: ALTIJD,
        acties: [{ soort: 'activeer-bewerking' }],
        parameters: [],
        uitleg: 'De eindstap. Blijft leeg, maar sluit de route af.',
    },
];
/**
 * De voorwaarde "geen fineer" wordt in de standaardset niet gebruikt, maar hij staat
 * hier omdat de editor hem als bouwsteen aanbiedt en een eigen bewerking hem nodig
 * kan hebben (bijvoorbeeld een schuurstap die alleen voor kale panelen draait).
 */
export { GEEN_FINEER };
//# sourceMappingURL=standaardregels.js.map